import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  useGetOrderByIdQuery,
  setOrderItemData,
  setTicketsSelected,
  useGetEventBySlugQuery,
  useGetAllEventsQuery,
  useGetBusinessBySubdomainQuery,
  clearReservation,
} from '../store';
import { parseFriendlyDate } from '../utils/dates';
import EventPageSkeleton from '../components/EventPageSkeleton';
import { setSelectedEvent } from '../store/slices/events';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import Carrousel from '../components/atoms/Carrousel/Carrousel';
import Ticket from '../components/Ticket';
import TicketDTO, { TicketPublicSaleStatus } from '../models/ticket';
import { EventPublicStatus } from '../models/event';
import CarrouselCards from '../components/atoms/CarrouselCards/CarrouselCards';
import CardsLottie from '../components/CardsLottie';
// import CarrouselCards from '../components/atoms/CarrouselCards/CarrouselCards';
import getScreenSize from '../utils/getScreenSize';
import ErrorPage from './ErrorPage';
import getSubdomain from '../utils/getSubdomain';
import usePageMetadata from '../hooks/usePageMetadata';
import CouponHeadsUp from '../components/CouponHeadsUp';
import renderTextWithLinks from '../utils/renderTextWithLinks';
import useQuery from '../hooks/useQuery';
import { PendingOrderStatus } from '../models/order';
import useEventPixel from '../hooks/useEventPixel';
import { trackViewContent } from '../utils/metaPixelTracking';
import { IconEnjoy3 } from '../components/icons';
import SimplifiedSalePage from './SimplifiedSalePage';
import BackgroundBannerImage from '../components/BackgroundBannerImage';

const EventPage: React.FC = () => {
  const { event_id: eventSlug } = useParams();
  const slug = eventSlug || '';
  const { data: event, isLoading: eventLoading } = useGetEventBySlugQuery(
    { slug },
    { skip: !slug },
  );
  const { data: events } = useGetAllEventsQuery({});
  const query = useQuery();
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const ticketRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [isCardOnScreen, setIsCardOnScreen] = useState(false);
  const [isTicketOnScreen, setIsTicketOnScreen] = useState(false);
  const isMobile = getScreenSize() === 'xs' || getScreenSize() === 'sm' || getScreenSize() === 'md';
  const subdomain = getSubdomain();
  const orderId = query.get('orderId');
  const { data: order } = useGetOrderByIdQuery({ orderId: orderId || '' }, { skip: !orderId });
  const { data: business } = useGetBusinessBySubdomainQuery(subdomain || '', { skip: !subdomain });

  usePageMetadata({ title: event?.name, description: event?.description });

  // Initialize event-specific pixel; useBasePixel already fires PageView to all pixels on load
  useEventPixel(event);

  useEffect(() => {
    if (event) {
      dispatch(setSelectedEvent(event));
    } else if (!eventLoading) {
      navigate('/');
    }
  }, [event, eventLoading, navigate, dispatch]);

  useEffect(() => {
    if (!event) {
      return;
    }
    const availableTickets = event.tickets?.filter(
      (t) => t.public_sale_status === TicketPublicSaleStatus.Available && t.public_sale_available,
    );
    const minPrice = availableTickets?.length
      ? Math.min(...availableTickets.filter((t) => !t.is_extra).map((t) => t.price))
      : 0;
    trackViewContent({
      content_name: event.name,
      content_type: 'product',
      content_ids: [event.id],
      value: minPrice,
    });
  }, [event?.id]);

  useEffect(() => {
    if (event) {
      const ref = query.get('ref');
      if (ref) {
        try {
          localStorage.setItem(`${event.id}_referral`, ref);
        } catch (e) {
          // ignore storage errors
        }
      }
    }
  }, [event, query]);

  useEffect(() => {
    if (event && !event.has_numerated_seats) {
      dispatch(clearReservation());
    }
  }, [dispatch, event]);

  useEffect(() => {
    if (orderId && order && (order.pending_status === PendingOrderStatus.no_reminder || order.pending_status === PendingOrderStatus.first_reminder || order.pending_status === PendingOrderStatus.second_reminder || order.pending_status === PendingOrderStatus.third_reminder)) {
      dispatch(setTicketsSelected(order.items.reduce((acc, item) => {
        const existingTicket = acc.find((t) => t.ticket.id === item.ticket.id);
        if (existingTicket) {
          existingTicket.quantity += 1;
          return acc;
        }
        return [...acc, { ticket: item.ticket, quantity: 1 }];
      }, [] as { ticket: TicketDTO; quantity: number }[])));

      dispatch(setOrderItemData(order.items.map((item) => ({
        ...item,
        ticket_id: item.ticket.id,
        ticket_extra: item.ticket.is_extra,
        seat_name: item?.event_seat?.name || '',
        seat_id: item?.event_seat?.id || '',
      }))));
      navigate(`/events/${event?.slug}/orders/new/checkout?email=${order.email}&link=${order.payments?.[0]?.checkout_link}`);
    }
  }, [orderId, order]);

  useEffect(() => {
    // Only set up observers when the component is fully mounted and refs are available
    const setupObservers = () => {
      const ticketObserver = new IntersectionObserver(
        ([entry]) => {
          setIsTicketOnScreen(entry.isIntersecting);
        },
        {
          root: null,
          threshold: event?.superfast_checkout ? 0.1 : 0.5,
        },
      );

      const cardsObserver = new IntersectionObserver(
        ([entry]) => {
          setIsCardOnScreen(entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0.2,
        },
      );

      if (ticketRef.current) {
        ticketObserver.observe(ticketRef.current);
      }

      if (cardsRef.current) {
        cardsObserver.observe(cardsRef.current);
      }

      return () => {
        ticketObserver.disconnect();
        cardsObserver.disconnect();
      };
    };

    // Set up observers after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(setupObservers, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [event]); // Re-run when event changes, which happens after data is loaded

  // Reset background image load state when the banner image changes

  const tickets = event?.tickets?.filter((t) => t.public_sale_status === TicketPublicSaleStatus.Available && t.public_sale_available);

  if (eventLoading) {
    return <EventPageSkeleton />;
  }

  if (!event || !tickets) {
    console.error('missing required data', { event, tickets });
    return <div className='absolute top-0 left-0 w-full h-full z-50'><ErrorPage /></div>;
  }

  const importantInfo = renderTextWithLinks(event.terms);

  const eventDescription = renderTextWithLinks(event.description);

  const handleClickBuyTickets = () => {
    if (event.superfast_checkout) {
      ticketRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/events/${event?.slug}/orders/new/${event?.has_numerated_seats ? 'seats' : 'tickets'}`);
    }
  };

  const minTicketPrice = Math.min(...tickets.filter((ticket) => ticket.is_extra === false).map((ticket) => ticket.price));

  return (
    <>
      <BackgroundBannerImage event={event} />
      <CouponHeadsUp />
      <div className="grid grid-cols-1 lg:grid-cols-11 gap-12 my-6 lg:my-12 lg:px-8 z-10">
        <div className={'relative flex flex-col gap-8 items-center lg:col-span-6 xl:col-span-7'}>
          <div className='relative w-full'>
            <div className={event.public_status !== EventPublicStatus.Ready ? 'grayscale w-full' : 'w-full col-span-7'}>
              <Carrousel
                images={[{ url: event.banner_image, eventId: event.id }]}
              />
            </div>
            {event.public_status === EventPublicStatus.NoTicketsAvailable
              && (
                <div className='absolute bottom-4 right-4 tag-warning z-10 text-center'>
                  Entradas agotadas
                </div>
              )}
          </div>
          <div className="flex flex-col gap-8 w-full">
            <div className='glass-surface p-6'>
              <div className="flex flex-col gap-0">
                <h1 className="text-[24px] lg:text-[48px] lg:leading-[1.2] font-semibold">{event.name}</h1>
                <h2 className="text-lg lg:text-[20px]">Organiza: {event.business_name}</h2>
              </div>
              <div className="flex flex-col gap-6 mt-6">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <h1 className="text-lg lg:text-[20px] font-semibold">Fecha y hora</h1>
                    <p>
                      {parseFriendlyDate(event.date, true, true)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <h1 className="text-lg lg:text-[20px] font-semibold">Ubicación</h1>
                    <div>
                      <p>{event.place}</p>
                      <p>{event.address}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <h1 className="text-lg lg:text-[20px] font-semibold">Público</h1>
                  <p>
                    {event.min_age
                      ? `Mayores de ${event.min_age} años`
                      : 'Apto para todo público'}
                  </p>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-6 glass-surface p-6'>
              <div className="flex flex-col gap-2">
                <h1 className="text-lg lg:text-[20px] font-semibold">Sobre el evento</h1>
                <div>{eventDescription}</div>
              </div>
              {importantInfo && (
                <div className="flex flex-col gap-2">
                  <h1 className="text-lg lg:text-[20px] font-semibold">Más información</h1>
                  <div>{importantInfo}</div>
                </div>
              )}
            </div>
            {event.maps_link && (
              <div className='flex flex-col gap-2 w-full glass-surface p-6'>
                <h1 className='text-lg lg:text-[20px] font-semibold'>Ver ubicación en el mapa</h1>
                <iframe
                  src={event.maps_link}
                  className="w-full mx-auto h-36 lg:h-64 rounded-lg"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            )}
          </div>
        </div>
        <div className="relative hidden lg:block lg:col-span-5 xl:col-span-4">
          {!event.superfast_checkout && <div ref={ticketRef} className="sticky top-24 z-10">
            <Ticket price={minTicketPrice} onClick={handleClickBuyTickets} event={event} />
          </div>}
          {event.superfast_checkout && <div>
            <SimplifiedSalePage eventpage />
          </div>}
        </div>

        {isMobile && (
          <>
            <div className={`fixed bottom-1 left-0 right-0 z-50 mx-4 ${isTicketOnScreen || isCardOnScreen ? 'hidden' : 'block'}`}>
              <Ticket price={minTicketPrice} onClick={handleClickBuyTickets} event={event} />
            </div>
            <div ref={ticketRef}>
              {!event.superfast_checkout && <Ticket price={minTicketPrice} onClick={handleClickBuyTickets} event={event} />}
              {event.superfast_checkout && <SimplifiedSalePage eventpage />}
            </div>
          </>
        )}
        <div className='flex flex-col gap-8 lg:gap-12 lg:col-span-11'>
          {!business?.is_white_label && <CardsLottie />}
          {!subdomain && <div>
            <div className="flex flex-row items-center gap-4">
              <span className="text-xl-semibold lg:display-lg-semibold text-text-primary">
                Te puede interesar
              </span>
              <IconEnjoy3 className="w-10 h-10" />
            </div>
            <div ref={cardsRef}><CarrouselCards cards={events?.filter((e) => e.id !== event.id).filter((e) => e.banner_display === true) || []} /></div>
          </div>}
        </div>
      </div>
    </>
  );
};

export default EventPage;
